import React, { ReactChild } from 'react';
import { AnimatePresence } from 'framer-motion';
import Details from './HBAccordionDetails';
import Summary from './HBAccordionSummary';
import { Accordion, AccordionItem } from './HBAccordion.styles';

export type Ttheme = 'plain' | 'regular' | 'light';

export interface HBAccordionProps {
    /** One or more `HBAccordion.Item` elements, each wrapping a `HBAccordion.Summary` and `HBAccordion.Details` pair in that order. */
    children: JSX.Element | JSX.Element[];
    /** ID of the currently expanded item; must match a Summary's `id` prop. Empty string means all collapsed. */
    expandedId?: string;
    /** Called when an item is clicked; receives the clicked item's `id`. Call with empty string to collapse all. */
    onClick?: (expandedId: string) => void;
    /** Custom icon shown on expanded items, replacing the default Minus icon. */
    collapseIcon?: ReactChild;
    /** Custom icon shown on collapsed items, replacing the default Plus icon. */
    expandedIcon?: ReactChild;
    /** Visual style variant: 'regular' has standard borders, 'plain' removes borders, 'light' uses lighter borders. */
    theme?: Ttheme;
    /** When true, items animate on initial mount; when false (default), only subsequent expand/collapse transitions animate. */
    enableInitialAnimation?: boolean;
}

const HBAccordionBase = ({
    children,
    expandedId = '',
    onClick,
    collapseIcon,
    expandedIcon,
    theme = 'regular',
    enableInitialAnimation = false
}: HBAccordionProps): any => {
    const toggle = (id: string) => onClick(id);

    return (
        <Accordion>
            {React.Children.map(children, child => {
                const { id } = child.props.children[0].props;
                const isExpanded = expandedId === id;
                const summaryItem = React.cloneElement(
                    child.props.children[0],
                    {
                        toggle,
                        isExpanded,
                        collapseIcon,
                        expandedIcon
                    }
                );

                return (
                    <AccordionItem theme={theme}>
                        {summaryItem}
                        <AnimatePresence initial={enableInitialAnimation}>
                            {isExpanded && child.props.children[1]}
                        </AnimatePresence>
                    </AccordionItem>
                );
            })}
        </Accordion>
    );
};

export const HBAccordion = Object.assign(HBAccordionBase, {
    Summary,
    Details,
    Item: ({ children }: { children: React.ReactNode }) => <>{children}</>
});

export default HBAccordion;
